import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import { useColumnLabels } from '../../ui/useColumnLabels.js'
import { useDeclaredColumns } from '../../ui/useDeclaredColumns.js'
import type { BoardColumn } from './boardColumns.js'
import { boardColumns } from './boardColumns.js'

/**
 * The contacts board's columns, and everything the header menu does to one.
 *
 * Two sources and one answer. A declared field carries its own label on its own
 * row; the six the board always has carry theirs in `contact_column_labels`,
 * because they are the contacts table's own columns and are not rows anywhere.
 * This is the one place that joins them, so nothing else has to know there are
 * two.
 *
 * Both halves are shared with the segments board, which has the same two kinds
 * of column: `useDeclaredColumns` for the ones an organisation creates, and
 * `useColumnLabels` for what it calls the ones the board came with. What is
 * this board's alone is joining them into one list.
 *
 * **Renaming cannot change who a campaign reaches.** A segment rule and a merge
 * token name a field's *key*, and no path here touches one.
 */
export function useBoardColumns({ client }: { readonly client: ApiClient }) {
  const declared = useDeclaredColumns({ client, subject: 'contacts' })
  const named = useColumnLabels({ client, subject: 'contacts' })

  const reload = useCallback(async () => {
    await Promise.all([declared.reload(), named.reload()])
  }, [declared.reload, named.reload])

  const edit = useCallback(
    (
      column: BoardColumn,
      change: {
        readonly label: string
        readonly type: ContactFieldDto['type']
        readonly choices: readonly string[]
      },
    ) => declared.edit(column.key ?? '', change),
    [declared.edit],
  )

  const remove = useCallback(
    (column: BoardColumn) => declared.remove(column.key ?? ''),
    [declared.remove],
  )

  return {
    fields: declared.fields,
    columns: boardColumns(declared.fields, named.labels),
    /**
     * Renames a column the board came with. Takes its id, because that is what
     * the label table is keyed on — a declared column is renamed by `edit`,
     * which also carries the kind and the choices it must not lose.
     */
    rename: named.rename,
    edit,
    remove,
    reload,
  }
}

/** The declaration behind a column, for the two calls that need its other half. */
export function fieldFor(
  fields: readonly ContactFieldDto[],
  column: BoardColumn,
): ContactFieldDto | undefined {
  return fields.find((field) => field.key === column.key)
}
